import React, { useCallback, useRef, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import CustomStepper from "../../../../../../shared/components/stepper/steppers";
import { InnerButton } from "../../../../../../shared/components/buttons/floating-button-container";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import CustomTable from "../../../../../../shared/components/table/custom-table";
import { ActionsMenu } from "../../../../../../shared/components/table/custom-actions";
import { MRT_ColumnDef } from "material-react-table";
import ActionsModalPopup, { ActionButtonConfig } from "../../../../../../shared/components/modals/action-popup";
import AlertPopup from "../../../../../../shared/components/modals/alert-moadal";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import RenderDetailPanel from "./render-detail-panel";
import IncomingTcInfo, { fetchSourceArticleMaps } from "./incoming-details/incoming-tc-info";
import ProductNetWeight from "./incoming-details/product-net-weight";
import IncomingTcStandard, { initIncomingTcStandardMap } from "./incoming-details/incoming-tc-standard";
import { INCOMING_TC_STEP_LABELS } from "./incoming-details/incoming-rmdf.utils";
import {
  findInvalidIncomingRawMaterialArticles,
  toCanonicalIncomingRawMaterialRows,
} from "./incoming-details/incoming-source-state.utils";
import { useApi } from "../../../../../../shared/hooks/useApi";
import {
  fetchIncomingTcDetailsList,
  fetchIncomingTcDetail,
  deleteIncomingTcDetail,
  hasCertTypeToken,
  IncomingTcEntry
} from "./incoming-details-apis";
import { IncomingTCInformation, IncomingProductNetWeightInfo } from "../../../tc-models/tc-req-dto/incoming-details.req.dto";
import { ProductShipmentResponse } from "../certified-products/certified-products.model";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { fetchProductaddDetails } from "../certified-products/product-api";
import {
  filterCertifiedIncomingRawMaterials,
  mergeIncomingRawMaterialsForDisplay,
  withIncomingRawMaterialKeys,
} from "./incoming-raw-material.utils";
import { roundTcV4Decimal } from "../../../tc-v4-decimals";
import { useTcTabErrorStore } from "../../../../../../store/tcTabErrors.store";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";

const initIncomingTcInfo: IncomingTCInformation = {
  tcAssociatedProductArticleNo: "",
  tcIncomingTCNo: "",
  tcIncomingATCNo:"",
  tcIncomingRMDFNo: "",
  tcIncomingFPFNo: "",
  tcIncomingTCStandard: [],
  tcIncomingProductNo: [],
  tcIncomingTcId: "",
  tcIncomingAtcId: "",
  tcIncomingLedgerTransactionId: "",
  tcIncomingFpfId: "",
  incomingProductStandardsByArticle: {},
  incomingProductSourceByArticle: {},
  incomingProductRawMaterialsByArticle: {},
  incomingProductMaxWeightByArticle: {},
  isFlagSeller: "No",
};

const initProductNetWeightInfo: IncomingProductNetWeightInfo = {
  netWeights: {},
  rawMaterials: {},
};

interface IncomingTcDetailsProps {
  onNextTab: () => Promise<void>;
}

const IncomingTcDetails: React.FC<IncomingTcDetailsProps> = ({ onNextTab }) => {
  const { notify } = useNotification();
  const { executeGet, executePost, executeDelete, setLoadingState } = useApi();
  const { TE_ID, formtype } = useParams<string>();
  const navigate = useNavigate();
  const { setTabError, setRmdfViolations } = useTcTabErrorStore();
  const { getCurrentFormValues } = useFormActions("Transaction-Details");
  const isReturn = getCurrentFormValues()?.tcIsReturn === "Yes";


  const [tableData, setTableData] = useState<IncomingTcEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteRowData, setDeleteRowData] = useState<IncomingTcEntry | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const incomingTcRef = useRef<any>(null);
  const incomingTcStandardRef = useRef<any>(null);
  const productWeightRef = useRef<any>(null);

  // Self-healing for the selection step: when the user reaches step 2 before
  // the source-article fetch has landed (or it failed), refetch here instead
  // of dead-ending on "No incoming products available".
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceAttempted, setSourceAttempted] = useState(false);
  const sourceFallbackKeyRef = useRef("");

  const [stepData, setStepData] = useState({
    incomingTcInfo: initIncomingTcInfo,
    incomingProductNetWeight: initProductNetWeightInfo,
    incomingTcStandard: initIncomingTcStandardMap as { [key: string]: string[] },
  });

  const normalizeStandards = (arr: any): string[] => {
    if (!Array.isArray(arr)) return [];
    
    let flattened: any[] = [];
    if (typeof (Array.prototype as any).flat === "function") {
      flattened = (arr as any).flat(Infinity).filter((v: any) => v !== null && v !== undefined);
    } else {
      flattened = (arr as any).reduce((acc: any[], cur: any) => 
        acc.concat(Array.isArray(cur) ? cur : [cur]), []
      ).filter((v: any) => v !== null && v !== undefined);
    }
    
    // Split concatenated standards (e.g., "GRSRCS" -> ["GRS", "RCS"])
    const result: string[] = [];
    flattened.forEach((item) => {
      const str = String(item).trim();
      if (!str) return;
      
      // Check if it's concatenated (common standards are 3-4 chars)
      if (str.length > 4 && !str.includes(',') && !str.includes(' ')) {
        // Try to split by known standard patterns
        const matches = str.match(/([A-Z]{2,4})/g);
        if (matches) {
          result.push(...matches);
        } else {
          result.push(str);
        }
      } else {
        result.push(str);
      }
    });
    
    // Remove duplicates
    return [...new Set(result)];
  };

  const normalizeStringList = (value: any): string[] => {
    const values = Array.isArray(value) ? value.flat(Infinity) : [value];
    return [...new Set(
      values
        .filter((item) => item !== null && item !== undefined)
        .map((item) => String(item).trim())
        .filter(Boolean)
    )];
  };

  const standardCodeFromValue = (value: any): string => {
    const text = String(value || "").trim();
    if (!text) return "";

    const parenthesizedCode = text.match(/\(([A-Z]{2,5})\)/);
    if (parenthesizedCode?.[1]) {
      return parenthesizedCode[1].toUpperCase();
    }

    const upperText = text.toUpperCase();
    const labelCodeMap: Record<string, string> = {
      "GLOBAL RECYCLED STANDARD": "GRS",
      "RECYCLED CLAIM STANDARD": "RCS",
      "ORGANIC CONTENT STANDARD": "OCS",
      "RESPONSIBLE WOOL STANDARD": "RWS",
      "RESPONSIBLE MOHAIR STANDARD": "RMS",
      "RESPONSIBLE ALPACA STANDARD": "RAS",
      "RESPONSIBLE DOWN STANDARD": "RDS",
      "CONTENT CLAIM STANDARD": "CCS",
      "MATERIALS MATTER": "MMS",
      "MATERIAL MATTER": "MMS",
      "GLOBAL ORGANIC TEXTILE STANDARD": "GOTS",
    };

    const matchedLabel = Object.keys(labelCodeMap).find((label) => upperText.includes(label));
    if (matchedLabel) {
      return labelCodeMap[matchedLabel];
    }

    if (/^[A-Z]{2,5}$/.test(upperText)) {
      return upperText;
    }

    const compact = upperText.replace(/[^A-Z]/g, "");
    if (compact.startsWith("GOTS")) return "GOTS";
    return compact.length >= 3 ? compact.slice(0, 3) : compact;
  };

  const normalizeStandardCodes = (value: any): string[] =>
    [...new Set(
      normalizeStringList(value)
        .flatMap((item) => String(item).split(","))
        .map(standardCodeFromValue)
        .filter(Boolean)
    )];

  const getIncomingNumber = (info: any): string =>
    info?.tcIncomingTCNo || info?.tcIncomingATCNo || info?.tcIncomingRMDFNo || info?.tcIncomingFPFNo || "";

  const roundWeight = (value: number): number => roundTcV4Decimal(Number(value) || 0);

  const getRawMaterialKey = (rm: any, index: number): string =>
    [
      rm.tcProductCategoryCode || "",
      rm.tcProductDetailCode || "",
      rm.rawMaterialCode || rm.tcProductIncomingCertifiedRawMaterialCode || "",
      Number(rm.rawMaterialPercentage || 0),
      index,
    ].join("|");

  const calculateIncomingRmWeight = (netWeightUsed: number, percentage: number, rm: any): number => {
    const sourceNet = Number(rm.tcProductNetShippingWeight || 0);
    const sourceSupplementary = Number(rm.tcProductSupplementaryWeight || 0);
    const sourceAdditives = Number(rm.tcProductAdditivesWeight || 0);
    const supplementaryUsed = sourceNet > 0 ? (sourceSupplementary / sourceNet) * netWeightUsed : 0;
    const additivesUsed = sourceNet > 0 ? (sourceAdditives / sourceNet) * netWeightUsed : 0;
    const productWeightForSplit = Math.max(Number(netWeightUsed || 0) - supplementaryUsed - additivesUsed, 0);
    return roundWeight((Number(percentage) / 100) * productWeightForSplit);
  };

  // const normalizeStandards = (arr: any[]): string[] => {
  //   if (!Array.isArray(arr)) return [];
  //   return arr.flat(Infinity).filter(v => v != null).map(String);
  // };

  const [incomingTcId, setIncomingTcId] = useState<string | null>(null);
  const [productData, setProductData] = useState<any[]>([]);
  const [oldProductId, setOldProductId] = useState<string | null>(null);
  const [originalArticleNo, setOriginalArticleNo] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const hasCreateTc: boolean = formtype === "create-tc";
  const hasSubmitTc: boolean = formtype === "submited-tc";
  const hasRejectedTc: boolean = formtype === "rejected-tc";

  useEffect(() => {
    if (TE_ID) {
      loadIncomingTcDetails();
      loadProductShipmentData();
    }
  }, [TE_ID, pagination?.pageIndex]);

  // Signal parent stepper to highlight the PRODUCT DETAILS tab pill orange when
  // RMDF violations exist, and populate the rmdfViolations map so that the
  // Product Information tab can render row-level field chips — exactly mirroring
  // the SC v4 facility-details / scStandard.store pattern.
  useEffect(() => {
    const violations = getRmdfViolations();
    const hasError = violations.length > 0;
    // Flag the 'product-details' tab (not 'incoming-tc-details') because that
    // is the tab the user must navigate to in order to fix the issue.
    setTabError('product-details', hasError);
    // Populate the per-article violation map so CertifiedProductsComponents
    // can show Chip badges without a second API call.
    const violationMap: Record<string, { missingFeedstock: boolean; missingReclaimedSource: boolean }> = {};
    violations.forEach((v) => {
      violationMap[v.productArticleNo] = {
        missingFeedstock: v.missingFeedstock,
        missingReclaimedSource: v.missingReclaimedSource,
      };
    });
    setRmdfViolations(violationMap);
  }, [JSON.stringify(tableData), JSON.stringify(productData)]);

  // When tcIncomingProductNo changes, clear standards map to prevent old product standards from persisting
  useEffect(() => {
    const selectedProducts = stepData.incomingTcInfo.tcIncomingProductNo;
    if (Array.isArray(selectedProducts) && selectedProducts.length === 0) {
      // Clear the standards map when no products are selected
      setStepData((prev) => ({
        ...prev,
        incomingTcStandard: {},
        incomingProductNetWeight: initProductNetWeightInfo,
      }));
    }
  }, [JSON.stringify(stepData.incomingTcInfo.tcIncomingProductNo)]);

  useEffect(() => {
    const selectedProducts = Array.isArray(stepData.incomingTcInfo.tcIncomingProductNo)
      ? stepData.incomingTcInfo.tcIncomingProductNo
      : [];
    if (selectedProducts.length === 0) return;

    const standardsByArticle = (stepData.incomingTcInfo as any).incomingProductStandardsByArticle || {};
    const sourceByArticle = (stepData.incomingTcInfo as any).incomingProductSourceByArticle || {};
    const rawMaterialsByArticle = (stepData.incomingTcInfo as any).incomingProductRawMaterialsByArticle || {};
    const isRMDF = Boolean((stepData.incomingTcInfo as any).tcIncomingRMDFNo);

    const formLevelStandards = normalizeStandards(
      Array.isArray(stepData.incomingTcInfo.tcIncomingTCStandard)
        ? stepData.incomingTcInfo.tcIncomingTCStandard
        : []
    );

    setStepData((prev) => {
      const nextMap = selectedProducts.reduce<{ [key: string]: string[] }>((acc, article) => {
        const mappedStandards = Array.isArray(standardsByArticle[article]) ? standardsByArticle[article] : [];
        acc[article] = mappedStandards.length > 0
          ? mappedStandards
          : isRMDF
          ? ["RMDF"]
          : prev.incomingTcStandard?.[article]?.length
          ? prev.incomingTcStandard[article]
          : formLevelStandards;
        return acc;
      }, {});
      const nextRawMaterials = { ...(prev.incomingProductNetWeight.rawMaterials || {}) };
      let rawMaterialsChanged = false;
      selectedProducts.forEach((article) => {
        const source = sourceByArticle[article];
        const sourceRows = Array.isArray(rawMaterialsByArticle[article]) && rawMaterialsByArticle[article].length > 0
          ? rawMaterialsByArticle[article]
          : source?.rawMaterialCode
          ? [source]
          : [];
        if (sourceRows.length > 0) {
          const storedRows = Array.isArray(nextRawMaterials[article])
            ? nextRawMaterials[article]
            : [];
          const mergedRows = mergeIncomingRawMaterialsForDisplay(sourceRows, storedRows).map(
            (row: any, index: number) => ({
              ...row,
              tcProductCategoryCode: row.tcProductCategoryCode || "",
              tcProductDetailCode: row.tcProductDetailCode || "",
              rawMaterialCode:
                row.rawMaterialCode ||
                row.tcProductIncomingCertifiedRawMaterialCode ||
                "",
              rawMaterialPercentage: Number(row.rawMaterialPercentage || 100),
              rawMaterialWeight: Number(
                row.rawMaterialWeight ??
                row.tcProductIncomingCertifiedRawMaterialWeightUsed ??
                0
              ),
              rawMaterialKey: row.rawMaterialKey || getRawMaterialKey(row, index),
              isCertified: row.isCertified ?? row.is_certified ?? "",
              tcProductNetShippingWeight: Number(row.tcProductNetShippingWeight || 0),
              tcProductSupplementaryWeight: Number(row.tcProductSupplementaryWeight || 0),
              tcProductAdditivesWeight: Number(row.tcProductAdditivesWeight || 0),
            })
          );
          if (JSON.stringify(storedRows) !== JSON.stringify(mergedRows)) {
            nextRawMaterials[article] = mergedRows;
            rawMaterialsChanged = true;
          }
        }
      });

      const incomingProductNetWeight = rawMaterialsChanged
        ? {
            ...prev.incomingProductNetWeight,
            rawMaterials: nextRawMaterials,
          }
        : prev.incomingProductNetWeight;

      return JSON.stringify(prev.incomingTcStandard) === JSON.stringify(nextMap) && !rawMaterialsChanged
        ? prev
        : { ...prev, incomingTcStandard: nextMap, incomingProductNetWeight };
    });
  }, [
    JSON.stringify(stepData.incomingTcInfo.tcIncomingProductNo),
    JSON.stringify((stepData.incomingTcInfo as any).incomingProductStandardsByArticle),
    JSON.stringify((stepData.incomingTcInfo as any).incomingProductSourceByArticle),
    JSON.stringify((stepData.incomingTcInfo as any).incomingProductRawMaterialsByArticle),
    JSON.stringify(stepData.incomingTcInfo.tcIncomingTCStandard),
    (stepData.incomingTcInfo as any).tcIncomingRMDFNo,
  ]);

  const handleIncomingSourceRowsChange = useCallback(
    (rowsByArticle: Record<string, any[]>) => {
      setStepData((prev) => {
        const info: any = prev.incomingTcInfo;
        const currentRows = info.incomingProductRawMaterialsByArticle || {};
        const nextRows = { ...currentRows, ...rowsByArticle };
        if (JSON.stringify(currentRows) === JSON.stringify(nextRows)) return prev;

        const nextSources = { ...(info.incomingProductSourceByArticle || {}) };
        Object.entries(rowsByArticle).forEach(([articleNo, rows]) => {
          if (Array.isArray(rows) && rows.length > 0) {
            nextSources[articleNo] = rows[0];
          }
        });
        return {
          ...prev,
          incomingTcInfo: {
            ...prev.incomingTcInfo,
            incomingProductRawMaterialsByArticle: nextRows,
            incomingProductSourceByArticle: nextSources,
          },
        };
      });
    },
    []
  );

  const loadProductShipmentData = async () => {
    try {
      const response = await executeGet(TCAppConfig.GET_LIST_PRODUCT_INFO_V4, { tcId: TE_ID });
      
      // Handle response - it might be a string, array, or undefined
      let responseArray: any[] = [];
      
      if (response?.data?.response) {
        // If response is a string, parse it
        if (typeof response.data.response === 'string') {
          try {
            responseArray = JSON.parse(response.data.response);
          } catch (parseError) {
            console.error("Failed to parse response string:", parseError);
            responseArray = [];
          }
        } 
        // If response is already an array, use it
        else if (Array.isArray(response.data.response)) {
          responseArray = response.data.response;
        }
        // Otherwise, default to empty array
        else {
          responseArray = [];
        }
      }
      
      // Fetch full details of each product so tcProductFeedstock and tcProductReclaimedMaterialSource are loaded
      const detailedList = await Promise.all(
        responseArray.map(async (p: any) => {
          try {
            const detailRes = await fetchProductaddDetails(executeGet, TE_ID!, p.productId, p.shipmentId);
            const detailData = detailRes?.data?.tcProductInformation?.[0] || detailRes?.data || {};
            return { ...p, ...detailData };
          } catch (err) {
            console.error("Failed to fetch product details inside incoming TC:", err);
            return p;
          }
        })
      );
      
      setProductData(detailedList);
    } catch (error) {
      console.error("Error fetching shipment data:", error);
    }
  };

  const loadIncomingTcDetails = async () => {
    if (!TE_ID) return;
    try {
      setIsLoading(true);
      const offset = pagination.pageIndex * pagination.pageSize;
      const data = await fetchIncomingTcDetailsList(
        executeGet,
        TE_ID,
        pagination.pageSize,
        offset
      );
      setTableData(data?.entries);
      setRowCount(data?.totalCount);
    } catch (error) {
      console.error("Error loading incoming TC details:", error);
      // notify("Failed to load incoming TC details", "error");
      setTableData([]);
      setRowCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const getProductShipmentDataByArticle = (list: ProductShipmentResponse[], articleNo: string) => {
    return list.find((item) => item.tcProductArticleNo === articleNo);
  };

  const resetDrawerState = () => {
    setStepData({
      incomingTcInfo: initIncomingTcInfo,
      incomingProductNetWeight: initProductNetWeightInfo,
      incomingTcStandard: initIncomingTcStandardMap,
    });
    setIncomingTcId(null);
    setOldProductId(null);
    setOriginalArticleNo(null);
    setIsCopy(false);
    setIsEdit(false);
    setActiveStep(0);
    setSourceLoading(false);
    setSourceAttempted(false);
    sourceFallbackKeyRef.current = "";
  };

  // Fallback loader for every incoming source type. Product data must always
  // come from incoming-tc-info after the canonical source ID is resolved.
  useEffect(() => {
    if (!drawerOpen || activeStep !== 1) return;
    const info: any = stepData.incomingTcInfo;

    const type = String(info.tcIncomingType || "").toUpperCase();
    const tcSourceId = info.tcIncomingTcId;
    const atcSourceId = (type === "ATU" || type === "GOTS")
      ? tcSourceId || info.tcIncomingAtcId
      : info.tcIncomingAtcId;
    const effectiveSourceId = tcSourceId || atcSourceId;
    const mapsPresent = [
      info.incomingProductRawMaterialsByArticle,
      info.incomingProductStandardsByArticle,
      info.incomingProductSourceByArticle,
    ].some((map: any) => Object.keys(map || {}).length > 0);

    if (mapsPresent) {
      setSourceAttempted(true);
      return;
    }
    if (!effectiveSourceId) return;
    if (!["V3", "V4", "ATU", "GOTS", "RMDF", "FPF"].includes(type)) {
      setSourceAttempted(true);
      return;
    }

    const fallbackKey = `${effectiveSourceId}|${type}`;
    if (sourceFallbackKeyRef.current === fallbackKey) return;
    sourceFallbackKeyRef.current = fallbackKey;

    setSourceAttempted(false);
    setSourceLoading(true);
    fetchSourceArticleMaps(executeGet, { type, tcId: effectiveSourceId })
      .then((sourceData) => {
        if (!sourceData) return;
        setStepData((prev) => ({
          ...prev,
          incomingTcInfo: {
            ...prev.incomingTcInfo,
            incomingProductStandardsByArticle: sourceData.maps.standardsByArticle,
            incomingProductSourceByArticle: sourceData.maps.sourceByArticle,
            incomingProductRawMaterialsByArticle: sourceData.maps.rawMaterialsByArticle,
            incomingProductMaxWeightByArticle: sourceData.maps.maxWeightByArticle,
            tcIncomingTCStandard:
              Array.isArray(prev.incomingTcInfo.tcIncomingTCStandard) &&
              prev.incomingTcInfo.tcIncomingTCStandard.length > 0
                ? prev.incomingTcInfo.tcIncomingTCStandard
                : sourceData.standards,
          },
        }));
      })
      .catch((error) => {
        console.error("Failed to load incoming source articles:", error);
      })
      .finally(() => {
        setSourceLoading(false);
        setSourceAttempted(true);
      });
  }, [drawerOpen, activeStep, stepData.incomingTcInfo]);

  const openDrawer = (copy = false, data?: IncomingTcEntry) => {
    if (data) {
      const standardsFromPayload = data.productNetWeight?.standards || {};
      const normalizedStandardsMap = Object.keys(standardsFromPayload || {}).length > 0
        ? Object.entries(standardsFromPayload).reduce<Record<string, string[]>>((acc, [article, standards]) => {
            acc[article] = normalizeStandards(standards);
            return acc;
          }, {})
        : {};

      const fallbackStandardsMap = (() => {
        const map: { [key: string]: string[] } = {};
        const standards = normalizeStandards(
          data.incomingTcInfo?.tcIncomingTCStandard || []
        ).filter((standard) => standard.toLowerCase() !== "na");
        const articles = normalizeStringList(
          data.incomingTcInfo?.tcIncomingProductNo || []
        ).filter((article) => article.toLowerCase() !== "na");
        articles.forEach((a) => {
          map[a] = [...standards];
        });
        return map;
      })();

      const incomingTcLicensingCode = (
        data.incomingTcInfo?.incomingTcLicensingCode || ""
      ).replace(/^CB-/, "");

      setStepData({
        incomingTcInfo: {
          ...data.incomingTcInfo,
          tcIncomingTCStandard: normalizeStandards(
            data.incomingTcInfo?.tcIncomingTCStandard
          ).filter((standard) => standard.toLowerCase() !== "na"),
          tcIncomingProductNo: normalizeStringList(
            data.incomingTcInfo?.tcIncomingProductNo || []
          ).filter((article) => article.toLowerCase() !== "na"),
          incomingTcLicensingCode:
            incomingTcLicensingCode.toLowerCase() === "na" ? "" : incomingTcLicensingCode,
          isFlagSeller: "No",
        },
        incomingProductNetWeight: data.productNetWeight,
        incomingTcStandard:
          Object.keys(normalizedStandardsMap).length > 0
            ? normalizedStandardsMap
            : fallbackStandardsMap,
      });
      setIncomingTcId(data.incomingTcId || null);
      setOldProductId(data.productId || null);
      setOriginalArticleNo(data.incomingTcInfo.tcAssociatedProductArticleNo || null);
    } else {
      resetDrawerState();
    }
    setIsCopy(copy);
    setIsEdit(!copy && Boolean(data));
    setDrawerOpen(true);
  };

  const handleAdd = () => openDrawer(false);

  const handleEdit = async (row: any) => {
    const incomingTcId = row.original.incomingTcId || row.original.incoming_tc_id;
    const productId = row.original.productId || row.original.product_id;
    try {
      const entry = await fetchIncomingTcDetail(executeGet, TE_ID!, incomingTcId, productId);
      if (entry) {
        openDrawer(false, entry);
      } else if (row.original.incomingTcProductInformation?.[0] || row.original.inputTcInformation?.[0]) {
        const info = row.original.incomingTcProductInformation?.[0] || row.original.inputTcInformation?.[0];
        const netWeights: { [key: string]: number } = {};
        const rawMaterials: { [key: string]: any[] } = {};
        const standardsMap: { [key: string]: string[] } = {};

        const weightInfos = info.incomingTcProductNetWeightUsedInformation || info.inputTcProductNetWeightUsedInformation || [];
        (Array.isArray(weightInfos) ? weightInfos : []).forEach((item: any) => {
          const articleNo = item.tcProductIncomingArticleNo || item.incomingArticleNo || item.articleNo || "";
          if (!articleNo) return;

          const netWeightValue = Number(
            item.tcProductIncomingArticleNetWeightUsed ??
            item.incomingTcProductNetWeightUsed ??
            0
          );
          if (netWeightValue > 0) {
            netWeights[articleNo] = netWeightValue;
          }

          const rawList = item.tcProductIncomingRawMaterialInformation || item.incomingTcRawMaterialInformation || [];
          if (Array.isArray(rawList) && rawList.length > 0) {
            rawMaterials[articleNo] = rawList.map((rm: any) => ({
              tcProductCategoryCode: rm.tcProductCategoryCode,
              tcProductDetailCode: rm.tcProductDetailCode,
              rawMaterialCode: rm.rawMaterialCode,
              rawMaterialPercentage: rm.rawMaterialPercentage,
            }));
          }

          const standardsForArticle = normalizeStandards(
            item.tcProductIncomingTcStandard || item.incomingProductStandard || []
          );
          if (standardsForArticle.length > 0) {
            standardsMap[articleNo] = standardsForArticle;
          }
        });

        const incomingProducts = Array.isArray(info.incomingProductArticleNo)
          ? info.incomingProductArticleNo
          : Object.keys(netWeights);

        const isSeller = (info.isSellerFarm === 'Yes' || info.isSeller === 'Yes' || info.isFlagSeller === 'Yes');
        const rawIncomingNo = String(info.tcProductIncomingTcNo || info.incomingTcNo || "");
        // Type tokens may sit anywhere in the number (RMDF_CUC_1 or CUC_RMDF_1).
        const isAtcIncoming = hasCertTypeToken(rawIncomingNo, "GOTS", "ATU");
        const isRmdfIncoming = hasCertTypeToken(rawIncomingNo, "RMDF");
        const isFpfIncoming = hasCertTypeToken(rawIncomingNo, "FPF");
        const incomingV4Issued = String(info.tcProductIncomingTcV4Issued || "").toUpperCase();
        const inferredIncomingType = isFpfIncoming
          ? "FPF"
          : isRmdfIncoming
          ? "RMDF"
          : isAtcIncoming
          ? (hasCertTypeToken(rawIncomingNo, "GOTS") ? "GOTS" : "ATU")
          : rawIncomingNo
          ? (incomingV4Issued === "Y" ? "V4" : "V3")
          : "";
        
        openDrawer(false, {
          id: row.original.id || 0,
          incomingTcId: incomingTcId || info.incomingTcNoId || row.original.incoming_tc_id || undefined,
          productId: productId || row.original.product_id,
          incomingTcInfo: {
            tcAssociatedProductArticleNo: info.tcAssociatedProductArticleNo || "",
            tcIncomingTCNo: !isAtcIncoming && !isRmdfIncoming && !isFpfIncoming ? rawIncomingNo : "",
            tcIncomingATCNo: isAtcIncoming ? rawIncomingNo : "",
            tcIncomingRMDFNo: isRmdfIncoming ? rawIncomingNo : "",
            tcIncomingFPFNo: isFpfIncoming ? rawIncomingNo : "",
            incomingTcLicensingCode: (
              info.incomingTcLicensingCode ||
              info.tcProductIncomingTcCbLicensingCode ||
              info.tc_product_incoming_tc_cb_licensing_code ||
              ""
            ).replace(/^CB-/, ""),
            tcIncomingTCStandard: normalizeStandards(
              info.incomingTcStandard || Object.values(standardsMap).flat()
            ),
            tcIncomingProductNo: isSeller ? ["na"] : incomingProducts,
            tcIncomingTcId: info.incomingTcNoId || "",
            tcIncomingAtcId: "",
            tcIncomingLedgerTransactionId: "",
            tcIncomingType: inferredIncomingType as any,
            tcProductIncomingTcV4Issued: incomingV4Issued === "Y" ? "Y" : incomingV4Issued === "N" ? "N" : "",
            isFlagSeller: isSeller ? "Yes" : "No",
          },
          productNetWeight: {
            netWeights,
            rawMaterials,
            standards: standardsMap,
          },
        } as IncomingTcEntry);
      } else {
        openDrawer(false, {
          id: row.original.id || 0,
          incomingTcId: incomingTcId,
          productId: productId || row.original.product_id,
          incomingTcInfo: {
            ...(row.original.incomingTcInfo || {}),
            tcAssociatedProductArticleNo: row.original.tcAssociatedProductArticleNo || row.original.tc_product_article_no || row.original.incomingTcInfo?.tcAssociatedProductArticleNo || "",
            tcIncomingTCNo: row.original.tcIncomingTCNo || row.original.tcIncomingATCNo || row.original.incomingTcInfo?.tcIncomingTCNo ||
            row.original.tcIncomingRMDFNo || row.original.incomingTcInfo?.tcIncomingRMDFNo || "",
            tcIncomingATCNo: row.original.tcIncomingATCNo || row.original.incomingTcInfo?.tcIncomingATCNo || "",
            tcIncomingRMDFNo: row.original.tcIncomingRMDFNo || row.original.incomingTcInfo?.tcIncomingRMDFNo || "",
            tcIncomingTCStandard: normalizeStandards(row.original.tcIncomingTCStandard || row.original.incomingTcInfo?.tcIncomingTCStandard || []),
            tcIncomingProductNo: normalizeStringList(row.original.tcIncomingProductNo || row.original.incomingTcInfo?.tcIncomingProductNo || []),
            incomingTcLicensingCode: (row.original.incomingTcInfo?.incomingTcLicensingCode || row.original.incomingTcInfo?.licensingCode || row.original.incomingTcInfo?.tcIncomingLicensingCode || "").replace(/^CB-/, ""),
            tcIncomingTcId: row.original.incomingTcInfo?.tcIncomingTcId || "",
            tcIncomingAtcId: row.original.incomingTcInfo?.tcIncomingAtcId || "",
            tcIncomingLedgerTransactionId: row.original.incomingTcInfo?.tcIncomingLedgerTransactionId || "",
            isFlagSeller: (row.original.incomingTcInfo?.isFlagSeller || row.original.isFlagSeller || "No") === "Yes" ? "Yes" : "No",
          },
          productNetWeight: row.original.productNetWeight || { netWeights: {}, rawMaterials: {} },
        } as IncomingTcEntry);
      }
    } catch (error) {
      console.error("Error fetching edit data:", error);
      notify("Failed to load data for editing", "error");
    }
  };

  const handleCopy = async (row: any) => {
    const incomingTcId = row.original.incomingTcId || row.original.incoming_tc_id;
    const productId = row.original.productId || row.original.product_id;
    try {
      const entry = await fetchIncomingTcDetail(executeGet, TE_ID!, incomingTcId, productId);
      if (entry) {
        openDrawer(true, entry);
      } else {
        openDrawer(true, {
          id: row.original.id || 0,
          incomingTcId: undefined,
          productId: row.original.product_id || row.original.productId,
          incomingTcInfo: row.original.incomingTcInfo || {
            tcAssociatedProductArticleNo: row.original.tcAssociatedProductArticleNo || row.original.tc_product_article_no || "",
            tcIncomingTCNo: row.original.tcIncomingTCNo || row.original.tcIncomingATCNo || row.original.tcIncomingRMDFNo || "",
            tcIncomingATCNo: row.original.tcIncomingATCNo || "",
            tcIncomingRMDFNo: row.original.tcIncomingRMDFNo || "",
            tcIncomingFPFNo: row.original.tcIncomingFPFNo || "",
            tcIncomingTCStandard: row.original.tcIncomingTCStandard || [],
            tcIncomingProductNo: row.original.tcIncomingProductNo || [],
            incomingTcLicensingCode: (row.original.incomingTcInfo?.incomingTcLicensingCode || row.original.licensingCode || "").replace(/^CB-/, ""),
            tcIncomingTcId: "",
            tcIncomingAtcId: "",
            tcIncomingLedgerTransactionId: "",
            isFlagSeller: (row.original.incomingTcInfo?.isFlagSeller || row.original.isFlagSeller || "No") === "Yes" ? "Yes" : "No",
          },
          productNetWeight: { netWeights: {}, rawMaterials: {}, standards: {} },
        } as IncomingTcEntry);
      }
    } catch (error) {
      console.error("Error fetching copy data:", error);
      notify("Failed to load data for copying", "error");
    }
  };

  const handleDelete = (row: any) => {
    console.log("handleDelete - Full row data:", row.original);
    
    // Extract incomingTcId from multiple possible locations
    const incomingTcId = 
      row.original.incomingTcId || 
      row.original.incoming_tc_id || 
      null;
    
    console.log("handleDelete - Extracted incomingTcId:", incomingTcId);
    
    setDeleteId(incomingTcId);
    setDeleteRowData(row.original);
    setDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    console.log("handleConfirmDelete invoked", { deleteId, deleteRowData });
    if (!TE_ID) return;
  
    const row = deleteRowData ?? tableData.find((item) => item.incomingTcId === deleteId);
    if (!row) {
      console.warn("No row found for deletion", { deleteId });
      setDialogOpen(false);
      return;
    }
  
    console.log("handleConfirmDelete - Full row data:", row);
  
    const resolvedIncomingTcId =
      row.incomingTcId ||
      deleteId ||
      null;
  
    console.log("handleConfirmDelete - Resolved incomingTcId:", resolvedIncomingTcId);
  
    const incomingTcNo =
      row.incomingTcInfo?.tcIncomingTCNo || 
      row.incomingTcInfo?.tcIncomingATCNo ||
      row.incomingTcInfo?.tcIncomingRMDFNo ||
      row.incomingTcInfo?.tcIncomingFPFNo ||
      (row as any).incomingTcNo ||
      (row as any).incoming_tc_no ||
      "";
    
    const productId = row.productId || (row as any).product_id || "";
    
    const productArticleNo =
      row.incomingTcInfo?.tcAssociatedProductArticleNo || 
      (row as any).tcAssociatedProductArticleNo ||
      (row as any).tc_product_article_no ||
      "";
  
    if (!resolvedIncomingTcId) {
      notify("Cannot delete: Incoming TC ID not found", "error");
      console.error("Missing incomingTcId for deletion");
      setDialogOpen(false);
      return;
    }
  
    try {
      setIsLoading(true);
      await deleteIncomingTcDetail(
        executeDelete,
        TE_ID,
        resolvedIncomingTcId,
        incomingTcNo,
        productId,
        productArticleNo
      );
      await loadIncomingTcDetails();
      notify("Deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting record:", error);
      notify("Failed to delete record", "error");
    } finally {
      setDialogOpen(false);
      setIsLoading(false);
      setDeleteId(null);
      setDeleteRowData(null);
    }
  };


const handleNext = async () => {
  const refToUse =
    activeStep === 0 ? incomingTcRef : activeStep === 1 ? incomingTcStandardRef : productWeightRef;

  // Product selection + standards happen on the checkbox-table step (step 2)
  if (activeStep === 1) {
    const standardsMap = stepData.incomingTcStandard || {};
    const selectedArticles = Array.isArray(stepData.incomingTcInfo.tcIncomingProductNo)
      ? stepData.incomingTcInfo.tcIncomingProductNo
      : [];

    if (selectedArticles.length === 0) {
      notify("Please select at least one incoming product article", "error");
      return;
    }

    const invalid = selectedArticles.filter(
      (article) =>
        !standardsMap[article] ||
        !Array.isArray(standardsMap[article]) ||
        standardsMap[article].length === 0
    );

    if (invalid.length > 0) {
      notify("At least one standard is required for each selected product article no", "error");
      return;
    }
  }

  const currentStepsLen = steps.length;

  if (refToUse?.current && typeof refToUse.current.validateForm === "function") {
    const errors = await refToUse.current.validateForm();

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      const errorMessage =
        typeof firstError === "string"
          ? firstError
          : Array.isArray(firstError)
          ? firstError[0]
          : "Please fill required fields correctly";

      notify(errorMessage, "error");

      if (refToUse.current.setTouched) {
        const touchedFields = Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {} as any);
        refToUse.current.setTouched(touchedFields);
      }
      return;
    }

    await refToUse.current.submitForm();
    setActiveStep((prev) => Math.min(prev + 1, currentStepsLen - 1));
  } else {
    setActiveStep((prev) => Math.min(prev + 1, currentStepsLen - 1));
  }
};

  const getRmdfViolations = () => {
    const violations: Array<{
      productArticleNo: string;
      productId: string;
      incomingTcNo: string;
      missingFeedstock: boolean;
      missingReclaimedSource: boolean;
    }> = [];

    if (!tableData || tableData.length === 0 || !productData || productData.length === 0) {
      return violations;
    }

    tableData.forEach((entry) => {
      const incomingTcInfo = entry.incomingTcInfo || {};
      const isRMDF = Boolean(incomingTcInfo.tcIncomingRMDFNo || (incomingTcInfo as any).tcIncomingType === "RMDF");
      if (!isRMDF) return;

      const articleNo = incomingTcInfo.tcAssociatedProductArticleNo;
      if (!articleNo) return;

      const product = productData.find((p) => p.tcProductArticleNo === articleNo);
      if (!product) return;

      const feedstock = product.tcProductFeedstock;
      const reclaimedSource = product.tcProductReclaimedMaterialSource;

      const missingFeedstock = !feedstock || (Array.isArray(feedstock) ? feedstock.length === 0 : String(feedstock).trim() === "");
      const missingReclaimedSource = !reclaimedSource || (Array.isArray(reclaimedSource) ? reclaimedSource.length === 0 : String(reclaimedSource).trim() === "");

      if (missingFeedstock || missingReclaimedSource) {
        violations.push({
          productArticleNo: articleNo,
          productId: product.productId || "",
          incomingTcNo: incomingTcInfo.tcIncomingRMDFNo || "",
          missingFeedstock,
          missingReclaimedSource,
        });
      }
    });

    return violations;
  };

  const handleNavigate = async () => {
    const violations = getRmdfViolations();
    if (violations.length > 0) {
      notify("Please configure Product Feedstock and Reclaimed Material Source in the Product Information tab for RMDF incoming TCs.", "error");
      return;
    }
    if(onNextTab){
      await onNextTab();
    }
    navigate(`/v4/tc/${formtype}/certified-standard-weight/${TE_ID}`);
  };

  // ========================================
  // HANDLE SAVE WITH 4 SCENARIOS
  // ========================================
  const handleFinish = async () => {
    if (!TE_ID) {
      notify("TE-ID missing", "error");
      return;
    }

    // Validate selected articles (tcIncomingProductNo is the array of selected incoming products)
    const selectedArticles = Array.isArray(stepData.incomingTcInfo.tcIncomingProductNo)
      ? stepData.incomingTcInfo.tcIncomingProductNo
      : [];

    if (selectedArticles.length === 0) {
      notify("Please select at least one Incoming Product Article Number", "error");
      return;
    }

    // Validate net weights for selected articles.
    // DAT-501 makes certified raw-material weights a TC v3-only direct-entry
    // rule. FPF and every other source require product net weight.
    const netWeights = stepData.incomingProductNetWeight.netWeights || {};
    const incomingSourceType = String((stepData.incomingTcInfo as any).tcIncomingType || "").toUpperCase();
    const isDirectFillIncoming = incomingSourceType === "V3";
    const rawMaterialWeightsMap = (stepData.incomingProductNetWeight as any).rawMaterialWeights || {};
    const rawMaterialsMap = stepData.incomingProductNetWeight.rawMaterials || {};
    const sourceByArticle = (stepData.incomingTcInfo as any).incomingProductSourceByArticle || {};
    const rawMaterialsByArticle = (stepData.incomingTcInfo as any).incomingProductRawMaterialsByArticle || {};
    const sourceRawMaterialsForArticle = (articleNo: string): any[] => {
      const savedRows = Array.isArray(rawMaterialsMap?.[articleNo])
        ? rawMaterialsMap[articleNo]
        : [];
      const sourceRows = Array.isArray(rawMaterialsByArticle?.[articleNo])
        ? rawMaterialsByArticle[articleNo]
        : [];
      const source = sourceByArticle[articleNo] || {};
      const rows = savedRows.length > 0
        ? savedRows
        : sourceRows.length > 0
        ? sourceRows
        : source.rawMaterialCode
        ? [source]
        : [];
      // Step 3 displays the complete source list. Only certified rows cross the
      // UI API boundary and can be persisted into the final TC JSON.
      return filterCertifiedIncomingRawMaterials(withIncomingRawMaterialKeys(rows));
    };
    const directWeightTotalForArticle = (articleNo: string): number => {
      const directWeights = rawMaterialWeightsMap?.[articleNo] || {};
      return sourceRawMaterialsForArticle(articleNo).reduce(
        (sum: number, rm: any, index: number) => {
          const key = rm.rawMaterialKey || getRawMaterialKey(rm, index);
          return sum + (Number(directWeights[key]) || 0);
        },
        0
      );
    };
    const missingWeights = selectedArticles.filter((art: string) => {
      return isDirectFillIncoming
        ? directWeightTotalForArticle(art) <= 0
        : !netWeights[art] || Number(netWeights[art]) <= 0;
    });
    if (missingWeights.length > 0) {
      notify(`${isDirectFillIncoming ? "Please enter raw material weight" : "Please enter net weight"} for selected products: ${missingWeights.join(", ")}`, "error");
      return;
    }

    // Get the primary article (tcAssociatedProductArticleNo is the main product)
    const articleNo = stepData.incomingTcInfo.tcAssociatedProductArticleNo;
    if (!articleNo) {
      notify("Please select Associated Product Article Number", "error");
      return;
    }

    const isRMDFIncoming = Boolean((stepData.incomingTcInfo as any).tcIncomingRMDFNo || (stepData.incomingTcInfo as any).tcIncomingType === "RMDF");
    if (isRMDFIncoming) {
      const product = productData.find((p) => p.tcProductArticleNo === articleNo);
      if (product) {
        const feedstock = product.tcProductFeedstock;
        const reclaimedSource = product.tcProductReclaimedMaterialSource;
        const missingFeedstock = !feedstock || (Array.isArray(feedstock) ? feedstock.length === 0 : String(feedstock).trim() === "");
        const missingReclaimedSource = !reclaimedSource || (Array.isArray(reclaimedSource) ? reclaimedSource.length === 0 : String(reclaimedSource).trim() === "");

        if (missingFeedstock || missingReclaimedSource) {
          notify(`Note: Product Feedstock and Reclaimed Material Source are required for Product ${articleNo} when incoming TC is an RMDF. Please configure them in the Product Information tab.`, "warning");
        }
      }
    }

    // Find shipment data for the selected article
    const shipment = getProductShipmentDataByArticle(productData, articleNo);
    if (!shipment) {
      notify("No shipment found for selected article", "error");
      return;
    }

    setLoadingState(true);

    // ========================================
    // SCENARIO DETECTION LOGIC
    // ========================================
    
    const isNewIncomingTC = !incomingTcId || incomingTcId === "";
    const currentProductId = shipment.productId;
    const currentArticleNo = stepData.incomingTcInfo.tcAssociatedProductArticleNo;
    
    // Check if product has changed from original
    const productChanged =
      originalArticleNo &&
      originalArticleNo !== currentArticleNo &&
      !isNewIncomingTC;

    let oldProductIdParam = currentProductId;
    let oldProductArticleNoParam = currentArticleNo;
    let sameIncomingTCNewProductCreate = "No";
    let scenario = "";

    // ========================================
    // DETERMINE SCENARIO
    // ========================================
    
    if (isNewIncomingTC) {
      // SCENARIO 1: New Incoming TC + New Product
      oldProductIdParam = currentProductId;
      oldProductArticleNoParam = currentArticleNo;
      sameIncomingTCNewProductCreate = "No";
      
    } else if (isCopy) {
      // SCENARIO 2: Old Incoming TC + New Product (Copy to different/same product)
      oldProductIdParam = currentProductId;
      oldProductArticleNoParam = currentArticleNo;
      sameIncomingTCNewProductCreate = "Yes";
      
    } else if (!productChanged) {
      // SCENARIO 3: Old Incoming TC + Old Product (Edit same incoming TC on same product)
      oldProductIdParam = oldProductId ?? currentProductId;
      oldProductArticleNoParam = originalArticleNo ?? currentArticleNo;
      sameIncomingTCNewProductCreate = "No";
      
    } else {
      // SCENARIO 4: Old Incoming TC + Different Product (Edit incoming TC and change product)
      oldProductIdParam = oldProductId ?? currentProductId;
      oldProductArticleNoParam = originalArticleNo ?? currentArticleNo;
      sameIncomingTCNewProductCreate = "No";
    }

    // ========================================
    // BUILD FINAL URL WITH QUERY PARAMETERS
    // ========================================
    
    const queryParams = new URLSearchParams({
      oldProductArticleNo: oldProductArticleNoParam,
      oldProductId: oldProductIdParam,
      sameIncomingTCNewProductCreate,
    });

    const finalUrl = `${TCAppConfig.POST_INCOMING_DETAIL_V4}?${queryParams.toString()}`;

    console.log("Final URL:", finalUrl);

    // ========================================
    // BUILD CLEAN PAYLOAD
    // ========================================
    
  const standardsMap = stepData.incomingTcStandard || {};
  const flattened = Object.values(standardsMap).reduce<string[]>(
    (acc, arr) => acc.concat(Array.isArray(arr) ? arr : []),
    []
  );
  const normalizedStandards = Array.from(new Set(flattened.map(String)));

  const standardsMapForArticles = stepData.incomingTcStandard || {};
  const isCurrentIncomingRMDF = Boolean((stepData.incomingTcInfo as any).tcIncomingRMDFNo);
  const netWeightEntries = selectedArticles.map((articleNo: string) => {
    const directWeightsForArticle = rawMaterialWeightsMap?.[articleNo] || {};
    const sourceRawMaterials = sourceRawMaterialsForArticle(articleNo);
    const directTotalWeight = directWeightTotalForArticle(articleNo);
    // Preserve the TC v4 five-decimal weight contract in the saved payload.
    const netWeightUsed = isDirectFillIncoming
      ? roundWeight(directTotalWeight)
      : roundWeight(Number(netWeights[articleNo]) || 0);
    const sourceForArticle = sourceByArticle[articleNo] || {};
    const perArticleStandards =
      Array.isArray(standardsMapForArticles[articleNo]) && standardsMapForArticles[articleNo].length > 0
        ? standardsMapForArticles[articleNo]
        : isCurrentIncomingRMDF
        ? ["RMDF"]
        : sourceForArticle.standard
        ? [sourceForArticle.standard]
        : normalizedStandards;
    const perArticleStandardCodes = normalizeStandardCodes(perArticleStandards);

    // Zero-weight rows are dropped instead of sent: a 0 "Incoming Certified Raw
    // Material Weight Used" means the raw material/lot was not used, and the
    // final-JSON validation rejects 0 at submit time (86ajcn0z8).
    const rawMaterialPayload = sourceRawMaterials
      .map((rm: any, index: number) => {
        const percentage = Number(rm.rawMaterialPercentage) || 0;
        const directWeight = Number(directWeightsForArticle[rm.rawMaterialKey || getRawMaterialKey(rm, index)] || 0);
        const rawMaterialWeight = isDirectFillIncoming
          ? roundWeight(directWeight)
          : calculateIncomingRmWeight(netWeightUsed, percentage, rm);
        return {
          tcProductCategoryCode: rm.tcProductCategoryCode || sourceForArticle.tcProductCategoryCode || "",
          tcProductDetailCode: rm.tcProductDetailCode || sourceForArticle.tcProductDetailCode || "",
          rawMaterialCode: rm.rawMaterialCode || sourceForArticle.rawMaterialCode || "",
          rawMaterialWeight,
          rawMaterialPercentage: percentage,
          tcProductIncomingCertifiedRawMaterialCode: rm.rawMaterialCode || sourceForArticle.rawMaterialCode || "",
          tcProductIncomingCertifiedRawMaterialWeightUsed: rawMaterialWeight,
        };
      })
      .filter((rm: any) => Number(rm.tcProductIncomingCertifiedRawMaterialWeightUsed) > 0);
    const primaryRawMaterial = rawMaterialPayload[0] || {};
    const canonicalRawMaterialPayload = toCanonicalIncomingRawMaterialRows(rawMaterialPayload);

    return {
      tcProductIncomingArticleNo: articleNo,
      tcProductIncomingArticleNetWeightUsed: netWeightUsed,
      tcProductIncomingTcStandard: perArticleStandardCodes,
      tcProductIncomingProductCategoryCode: primaryRawMaterial.tcProductCategoryCode || "",
      tcProductIncomingProductDetailCode: primaryRawMaterial.tcProductDetailCode || "",
      tcProductIncomingRawMaterialInformation: canonicalRawMaterialPayload,
    };
  });

  const invalidRawMaterialArticles = findInvalidIncomingRawMaterialArticles(netWeightEntries);
  if (invalidRawMaterialArticles.length > 0) {
    setLoadingState(false);
    notify(
      `Incoming raw material data must include category, detail, material code, and a positive weight for: ${invalidRawMaterialArticles.join(", ")}`,
      "error"
    );
    return false;
  }

  const licensingRaw = (stepData.incomingTcInfo as any).incomingTcLicensingCode || (stepData.incomingTcInfo as any).licensingCode || "";
  let licensingCodeOnly = String(licensingRaw || "").trim();
  if (licensingCodeOnly && !licensingCodeOnly.startsWith("CB-")) {
    licensingCodeOnly = `CB-${licensingCodeOnly}`;
  }

    const cleanPayload = {
      tcId: TE_ID,
      productId: currentProductId,
      ...(isNewIncomingTC ? {} : { incomingTcId: incomingTcId }),
      incomingTcProductInformation: [
        {
          tcAssociatedProductArticleNo: stepData.incomingTcInfo.tcAssociatedProductArticleNo,
          tcProductIncomingTcNo: getIncomingNumber(stepData.incomingTcInfo),
          tcProductIncomingTcV4Issued: (stepData.incomingTcInfo as any).tcProductIncomingTcV4Issued || (incomingSourceType === "V4" ? "Y" : "N"),
          tcProductIncomingTcCbLicensingCode: licensingCodeOnly,
          isSellerFarm: 'No',
          incomingTcNoId: stepData.incomingTcInfo.tcIncomingTcId || stepData.incomingTcInfo.tcIncomingAtcId || stepData.incomingTcInfo.tcIncomingLedgerTransactionId,
          incomingTcProductNetWeightUsedInformation: netWeightEntries,
        },
      ],
    };


    // ========================================
    // EXECUTE API CALL
    // ========================================
    
    try {
      const result = await executePost(finalUrl, cleanPayload);
      setLoadingState(false);

      if (result?.status === 200) {
        notify("Data Saved Successfully", "success");
        
        setDrawerOpen(false);
        resetDrawerState();
        await loadIncomingTcDetails();
        return true;
      } else {
        notify("Unable to Save", "error");
        setLoadingState(false);
        return false;
      }
    } catch (err: any) {
      console.error(`Error saving incoming TC in ${scenario}:`, err);
      setLoadingState(false);
      notify(err?.message || "Failed to save incoming TC", "error");
      return false;
    }
  };

// The stepper/dialog wording follows the chosen certificate type — showing
// "Incoming TC ..." while attaching an ATU/RMDF/FPF/v3 reference confused
// testers (86ajfgrcn). FPF references are lots, not product articles.
const incomingTypeToken = String(
  (stepData.incomingTcInfo as any).tcIncomingType || ""
).toUpperCase();
const incomingKindLabel = incomingTypeToken.startsWith("FPF")
  ? "FPF"
  : incomingTypeToken.startsWith("ATU")
  ? "ATU"
  : incomingTypeToken.startsWith("RMDF")
  ? "RMDF"
  : incomingTypeToken.startsWith("GOTS")
  ? "GOTS"
  : "TC";

const steps = [
  {
    label: INCOMING_TC_STEP_LABELS[0],
    content: (
      <IncomingTcInfo
        formRef={incomingTcRef}
        componentData={stepData.incomingTcInfo}
        setComponentData={(data: IncomingTCInformation) =>
          setStepData((prev) => {
            // A payload that names a map key owns it — even an EMPTY map must win
            // when the certificate number/type changed, otherwise stale articles
            // from the previous source keep showing in the selection table.
            // Payloads that omit the key (e.g. seller toggle) keep the previous maps.
            const { __replaceMaps, ...cleanData } = data as any;
            const keepMap = (key: string, previousMap: any) =>
              __replaceMaps || Object.prototype.hasOwnProperty.call(cleanData, key)
                ? cleanData[key] || {}
                : previousMap || {};
            return {
              ...prev,
              incomingTcInfo: {
                ...prev.incomingTcInfo,
                ...cleanData,
                incomingProductStandardsByArticle: keepMap(
                  "incomingProductStandardsByArticle",
                  (prev.incomingTcInfo as any).incomingProductStandardsByArticle
                ),
                incomingProductSourceByArticle: keepMap(
                  "incomingProductSourceByArticle",
                  (prev.incomingTcInfo as any).incomingProductSourceByArticle
                ),
                incomingProductRawMaterialsByArticle: keepMap(
                  "incomingProductRawMaterialsByArticle",
                  (prev.incomingTcInfo as any).incomingProductRawMaterialsByArticle
                ),
                incomingProductMaxWeightByArticle: keepMap(
                  "incomingProductMaxWeightByArticle",
                  (prev.incomingTcInfo as any).incomingProductMaxWeightByArticle
                ),
              },
            };
          })
        }
      />
    )
  },
  {
    label: INCOMING_TC_STEP_LABELS[1],
    content: (
      <IncomingTcStandard
        formRef={incomingTcStandardRef}
        componentData={stepData.incomingTcStandard}
        setComponentData={(data: { [key: string]: string[] }) =>
          setStepData((prev) => ({ ...prev, incomingTcStandard: data }))
        }
        selectedArticles={Array.isArray(stepData.incomingTcInfo.tcIncomingProductNo) ? stepData.incomingTcInfo.tcIncomingProductNo : []}
        onSelectionChange={(articles: string[]) =>
          setStepData((prev) => ({
            ...prev,
            incomingTcInfo: {
              ...prev.incomingTcInfo,
              tcIncomingProductNo: articles,
            },
          }))
        }
        allArticles={(() => {
          const info: any = stepData.incomingTcInfo;
          const keys = new Set<string>([
            ...Object.keys(info.incomingProductRawMaterialsByArticle || {}),
            ...Object.keys(info.incomingProductStandardsByArticle || {}),
            ...Object.keys(info.incomingProductSourceByArticle || {}),
            ...Object.keys(info.incomingProductMaxWeightByArticle || {}),
            ...(Array.isArray(info.tcIncomingProductNo) ? info.tcIncomingProductNo : []),
          ]);
          keys.delete("");
          keys.delete("na");
          return Array.from(keys);
        })()}
        standardsByArticle={(stepData.incomingTcInfo as any).incomingProductStandardsByArticle || {}}
        rawMaterialsByArticle={(stepData.incomingTcInfo as any).incomingProductRawMaterialsByArticle || {}}
        sourceByArticle={(stepData.incomingTcInfo as any).incomingProductSourceByArticle || {}}
        incomingTcType={(stepData.incomingTcInfo as any).tcIncomingType || ""}
        fallbackStandards={normalizeStandards(
          Array.isArray(stepData.incomingTcInfo.tcIncomingTCStandard)
            ? stepData.incomingTcInfo.tcIncomingTCStandard
            : []
        )}
        effectiveTcId={
          stepData.incomingTcInfo.tcIncomingTcId ||
          stepData.incomingTcInfo.tcIncomingAtcId ||
          stepData.incomingTcInfo.tcIncomingLedgerTransactionId ||
          ""
        }
        sourceLoading={sourceLoading || !sourceAttempted}
        onSourceRowsChange={handleIncomingSourceRowsChange}
      />
    ),
  },
  {
    label: INCOMING_TC_STEP_LABELS[2],
    content: (
      <ProductNetWeight
        formRef={productWeightRef}
        componentData={stepData.incomingProductNetWeight}
        setComponentData={(data: IncomingProductNetWeightInfo) =>
          setStepData((prev) => ({ ...prev, incomingProductNetWeight: data }))
        }
        selectedArticles={Array.isArray(stepData.incomingTcInfo.tcIncomingProductNo) ? stepData.incomingTcInfo.tcIncomingProductNo : []}
        incomingTcId={stepData.incomingTcInfo.tcIncomingTcId}
        incomingAtcId={stepData.incomingTcInfo.tcIncomingAtcId}
        incomingLedgerTransactionId={stepData.incomingTcInfo.tcIncomingLedgerTransactionId}
        incomingTcType={(stepData.incomingTcInfo as any).tcIncomingType || ""}
        standardsByArticle={stepData.incomingTcStandard || (stepData.incomingTcInfo as any).incomingProductStandardsByArticle || {}}
      />
    ),
  },
];

  const columns: MRT_ColumnDef<IncomingTcEntry>[] = [
    { accessorKey: "incomingTcInfo.tcAssociatedProductArticleNo", header: "Associated Product Article Number" },
    {
      accessorKey: "incomingTcInfo.tcIncomingTCNo",
      header: "Incoming TC/ATU/RMDF/FPF Number",
      Cell: ({ row }) => {
        const atc = row.original.incomingTcInfo?.tcIncomingATCNo || "";
        const tc = row.original.incomingTcInfo?.tcIncomingTCNo || "";
        const rmdf = row.original.incomingTcInfo?.tcIncomingRMDFNo || "";
        const fpf = row.original.incomingTcInfo?.tcIncomingFPFNo || "";
        if (atc) return `${atc}`;
        if (tc) return `${tc}`;
        if (rmdf) return `${rmdf}`;
        if (fpf) return `${fpf}`;
        return "N/A";
      },
    },
    {
      accessorKey: "incomingTcInfo.tcIncomingTCStandard",
      header: "Incoming TC Standard",
      Cell: ({ row }) =>{
        const standards = row.original?.incomingTcInfo?.tcIncomingTCStandard;

        if (Array.isArray(standards)) {
          // If array is empty, show "na"
          if (standards.length === 0) {
            return "na";
          }
          // Check if all elements are "na"
          const allNa = standards.every((s: any) => String(s).toLowerCase() === 'na');
          if (allNa) {
            return "na";
          }
          // Filter out "na" values and join the rest
          const filtered = standards.filter((s: any) => String(s).toLowerCase() !== 'na');
          return filtered.length > 0 ? filtered.join(", ") : "na";
        }
        
        return standards || "na";
      },
    },
  ];

  const stepsToUse = steps;

  const actions: ActionButtonConfig[] = [];
  if (activeStep !== 0) {
    actions.push({
      label: 'Back',
      onClick: () => setActiveStep((prev) => Math.max(prev - 1, 0)),
      variant: 'outlined',
      color: 'default',
      disabled: activeStep === 0,
    });
  }
  actions.push({
    label: activeStep === stepsToUse.length - 1 ? 'Save' : 'Next',
    onClick: activeStep === stepsToUse.length - 1 ? handleFinish : handleNext,
    variant: 'contained',
    color: 'primary',
  });

  if (isLoading && tableData.length === 0) return <LoaderComponent />;

  const rmdfViolations = getRmdfViolations();

  return (
    <Box>
      {rmdfViolations.length > 0 && (
        <Box mb={2} sx={{ 
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1,
          bgcolor: "#fffbf0", 
          border: "1px solid #e8a000", 
          borderRadius: "8px", 
          px: 2, py: 1.5
        }}>
          <Typography variant="body2" sx={{ color: "#7a4f00", fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
            <span>ÔÜá</span> Product Details Need Attention ({rmdfViolations.length}):
          </Typography>
          <Box sx={{ width: "100%", mt: 0.5 }}>
            {rmdfViolations.map((v, i) => (
              <Typography key={i} variant="caption" sx={{ display: "block", color: "#7a4f00" }}>
                ÔÇó Product <strong>{v.productArticleNo}</strong>: Reclaimed material details are missing (
                {v.missingFeedstock && v.missingReclaimedSource
                  ? "Feedstock and Reclaimed Source are required"
                  : v.missingFeedstock
                  ? "Feedstock is required"
                  : "Reclaimed Source is required"}
                ) because the linked incoming certificate <strong>{v.incomingTcNo}</strong> is an RMDF. Please update this product in the Product Information tab.
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {isReturn && (
        <Box mb={2} sx={{
          display: "flex",
          alignItems: "center",
          bgcolor: "#e5f6fd",
          border: "1px solid #0288d1",
          borderRadius: "4px",
          px: 2,
          py: 1.5,
        }}>
          <Typography variant="body2" sx={{ color: "#01579b", fontWeight: 500 }}>
            For return TCs, the incoming TC details are auto-filled based on the original TC. Please review the pre-filled entries below or add details for any missing return products.
          </Typography>
        </Box>
      )}

      {!(hasSubmitTc) && (
        <Box display="flex" justifyContent="flex-end" mb={3}>
          <StyledButton variant="contained" onClick={handleAdd}>
            Add Incoming TC Information
          </StyledButton>
        </Box>
      )}

      <CustomTable
        columns={columns}
        data={tableData}
        enableRowActions={!hasSubmitTc}
        enableTopToolbar={false}
        enableBottomToolbar={true}
        rowCount={rowCount}
        paginations={pagination}
        setPaginations={setPagination}
        renderDetailPanel={({ row }) => <RenderDetailPanel row={row} />}
        renderRowActions={({ row }) => (
          <ActionsMenu
            row={row}
            onEdit={handleEdit}
            // onCopy={handleCopy}
            onDelete={handleDelete}
          />
        )}
      />

      <ActionsModalPopup
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          resetDrawerState();
        }}
        title={`${isEdit ? "Edit" : "Add"} Incoming ${incomingKindLabel} Information`}
        actions={actions}
        minHeight="auto"
      >
        <CustomStepper
          steps={stepsToUse}
          activeStep={activeStep}
          onStepChange={setActiveStep}
        />
      </ActionsModalPopup>

      <AlertPopup
        open={dialogOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete this ${deleteRowData?.incomingTcInfo?.tcAssociatedProductArticleNo } Incoming TC?`}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Yes"
        cancelText="Cancel"
      />

      {!drawerOpen && (
        <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
          <NavLink to="/v4/tc-dashboard">
            <InnerButton>Home</InnerButton>
          </NavLink>
                <NavLink to={`/v4/tc/${formtype}/product-details/${TE_ID}`}>
                  <InnerButton>Back</InnerButton>
          </NavLink>
          {tableData?.length > 0 && (
                <InnerButton onClick={handleNavigate}>Next</InnerButton>
              )}
        </Box>
      )}
    </Box>
  );
};

export default IncomingTcDetails;
